import React, { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ThemeContext } from 'styled-components';

import { DsTypography } from 'componentsV2/DS';

import { OFFER_COUNTDOWN_BAR_SLOT_ID } from '../../constants/slots';
import {
  OFFER_COUNTDOWN_TEXTS,
  OFFER_REMAINING_SLOTS_TEXTS,
} from '../../constants/texts';
import { formatCountdown } from '../../utils/formatCountdown';

/**
 * FIN-8011: faixa de largura total com o contador da oferta, logo abaixo do
 * "Atendimento". Vai para o slot do AppRoot (acima do logo); sem o slot,
 * renderiza no lugar. FIN-8239: mostra "Encerra em ..." e/ou "Restam N vagas"
 * (separados por um traço vertical quando há os dois); `remainingMs` e
 * `remainingSlots` null = não renderiza. Abaixo de 400px os dois não cabem
 * numa linha (320px): empilham, sem o traço.
 */
export function OfferCountdownTopBar({
  remainingMs,
  remainingSlots = null,
}: {
  remainingMs: number | null;
  remainingSlots?: number | null;
}) {
  // Sem provider (testes), cai na variável CSS do DS.
  const theme = useContext(ThemeContext);
  // undefined = ainda não procurou o slot (SSR e primeiro render).
  const [slot, setSlot] = useState<HTMLElement | null | undefined>(undefined);

  useEffect(() => {
    setSlot(document.getElementById(OFFER_COUNTDOWN_BAR_SLOT_ID));
  }, []);

  const hasCountdown = remainingMs !== null;
  const hasSlots = remainingSlots !== null;

  if ((!hasCountdown && !hasSlots) || slot === undefined) return null;

  const formattedRemaining = hasCountdown ? formatCountdown(remainingMs) : '';

  // Cor da marca (secondary-pure do tema do produto), como os destaques do
  // checkout. O fundo opaco (cor da página) vem do slot sticky no AppRoot.
  const brandColor =
    theme?.color?.secondary?.pure ?? 'var(--color-secondary-pure)';

  const bar = (
    <div className="w-full">
      <div
        className="flex w-full flex-col items-center justify-center gap-1 border-b-2 px-4 py-3 text-center min-[400px]:flex-row min-[400px]:gap-4"
        style={{
          borderColor: brandColor,
          backgroundColor: `color-mix(in srgb, ${brandColor} 8%, transparent)`,
        }}
        data-testid="offer-countdown-top-bar"
      >
        {hasCountdown && (
          <div
            className="flex items-center gap-2 whitespace-nowrap"
            role="timer"
            aria-label={`${OFFER_COUNTDOWN_TEXTS.label} ${formattedRemaining}`}
          >
            <DsTypography
              component="paragraph"
              size="small"
              color="neutral-pure"
            >
              {OFFER_COUNTDOWN_TEXTS.label}
            </DsTypography>
            <DsTypography
              component="paragraph"
              size="small-bold"
              color="secondary-pure"
            >
              {formattedRemaining}
            </DsTypography>
          </div>
        )}
        {hasCountdown && hasSlots && (
          <div
            className="hidden w-px self-stretch min-[400px]:block"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}
            aria-hidden="true"
          />
        )}
        {hasSlots && (
          <div
            className="flex items-center gap-2 whitespace-nowrap"
            data-testid="offer-top-bar-slots"
          >
            <DsTypography
              component="paragraph"
              size="small"
              color="neutral-pure"
            >
              {OFFER_REMAINING_SLOTS_TEXTS.barLabel(remainingSlots)}
            </DsTypography>
            <DsTypography
              component="paragraph"
              size="small-bold"
              color="secondary-pure"
            >
              {OFFER_REMAINING_SLOTS_TEXTS.slots(remainingSlots)}
            </DsTypography>
          </div>
        )}
      </div>
    </div>
  );

  return slot ? createPortal(bar, slot) : bar;
}
